import React from "react";
import { Link } from "react-router-dom";
import { HeartPulse, ArrowRight, Play, CheckCircle2, Star, CalendarPlus, Smile, Briefcase, Award, MapPin, Phone, Mail, Clock } from "lucide-react";
import HeroCarousel from "../components/HeroCarousel";
import { aboutShort, featureStrip, services, testimonials, stats, articles, serviceOptionsForForm, siteInfo } from "../data/mock";

const statIcons = [CalendarPlus, Smile, Briefcase, Award];

export default function Home() {
  return (
    <div>
      <HeroCarousel />

      {/* Feature strip */}
      <section className="relative -mt-10 md:-mt-14 z-10 px-4">
        <div className="max-w-7xl mx-auto bg-gradient-to-r from-[#b81d24] via-[#cf3a26] to-[#e58a25] rounded-2xl shadow-2xl p-6 md:p-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featureStrip.map((label, i) => (
              <div key={i} className="flex flex-col items-center text-center text-white">
                <div className="w-16 h-16 rounded-full bg-amber-400 flex items-center justify-center shadow-md mb-3">
                  <HeartPulse className="w-8 h-8 text-[#1a1a1a]" />
                </div>
                <p className="font-bold text-lg leading-snug">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-16 h-16 bg-amber-400 rounded-lg flex items-center justify-center shadow-lg z-10">
              <Play className="w-7 h-7 text-[#1a1a1a] fill-[#1a1a1a]" />
            </div>
            <img
              src={aboutShort.image}
              alt="Dr. Mamtesh Gupta"
              className="w-full rounded-2xl shadow-xl object-cover object-top h-[480px]"
            />
          </div>
          <div>
            <p className="font-script text-emerald-700 text-2xl mb-2">About Us</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">{aboutShort.heading}</h2>
            <div className="flex items-center gap-2 mb-5">
              <span className="h-[3px] w-10 bg-amber-400"></span>
              <HeartPulse className="w-5 h-5 text-[#b81d24]" />
              <span className="h-[3px] w-10 bg-amber-400"></span>
            </div>
            <p className="text-gray-600 leading-relaxed mb-6">{aboutShort.paragraph}</p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 bg-[#b81d24] hover:bg-[#9a1820] text-white font-bold px-6 py-3 rounded-full transition-colors"
            >
              More About Us
              <span className="w-6 h-6 rounded-full bg-amber-400 text-[#b81d24] flex items-center justify-center">
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>

        <div className="max-w-7xl mx-auto mt-12 bg-gradient-to-r from-[#b81d24] to-[#e58a25] rounded-2xl p-8 md:p-10 grid grid-cols-1 md:grid-cols-3 gap-8 text-white">
          {aboutShort.highlights.map((h, i) => (
            <div key={i}>
              <div className="w-10 h-10 rounded-full bg-amber-400 text-[#1a1a1a] flex items-center justify-center mb-3">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <h4 className="font-bold text-xl mb-2">{h.title}</h4>
              <p className="text-white/90 text-sm leading-relaxed">{h.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Expertise */}
      <section className="py-20 px-4 bg-[#fbf6ee]">
        <div className="max-w-7xl mx-auto text-center mb-12">
          <p className="font-script text-emerald-700 text-2xl mb-2">Expertise?</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">We Provide Best Cardiac Services</h2>
          <div className="flex items-center justify-center gap-2 mt-4">
            <span className="h-[3px] w-10 bg-amber-400"></span>
            <HeartPulse className="w-5 h-5 text-[#b81d24]" />
            <span className="h-[3px] w-10 bg-amber-400"></span>
          </div>
        </div>
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.slice(0, 4).map((s) => (
            <Link
              to={`/services/${s.slug}`}
              key={s.slug}
              className="group relative bg-[#b81d24] text-white rounded-2xl p-7 pt-14 hover:bg-[#9a1820] transition-colors shadow-md hover:shadow-2xl hover:-translate-y-1 duration-300"
            >
              <div className="absolute -top-7 left-1/2 -translate-x-1/2 w-14 h-14 rounded-full bg-amber-400 flex items-center justify-center shadow-md">
                <HeartPulse className="w-7 h-7 text-[#1a1a1a]" />
              </div>
              <h3 className="text-xl font-bold text-center mb-3">{s.title}</h3>
              <div className="w-12 h-[3px] bg-amber-400 mx-auto mb-4" />
              <p className="text-white/85 text-sm text-center min-h-[80px]">{s.short}</p>
              <div className="mt-5 flex justify-center">
                <span className="w-10 h-10 rounded-full border border-white/50 flex items-center justify-center group-hover:bg-amber-400 group-hover:border-amber-400 group-hover:text-[#1a1a1a] transition-colors">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/expertise" className="inline-flex items-center gap-2 text-[#b81d24] font-bold hover:underline">
            View All Services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1">
            <p className="font-script text-emerald-700 text-2xl mb-2">Testimonials</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6">What Our Patients Are Saying</h2>
            <div className="bg-amber-50 rounded-2xl p-6 border border-amber-100">
              <p className="text-gray-500 text-sm mb-1">Trust By Patients</p>
              <div className="flex items-end gap-2">
                <HeartPulse className="w-10 h-10 text-[#b81d24]" />
                <span className="text-4xl font-extrabold text-emerald-700">100,000+</span>
              </div>
              <div className="mt-3 flex items-center gap-1 text-amber-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-500" />
                ))}
              </div>
            </div>
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
            {testimonials.slice(0, 4).map((t) => (
              <div key={t.id} className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow">
                <p className="text-gray-600 text-sm leading-relaxed mb-5 italic">“{t.text}”</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 font-bold flex items-center justify-center">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{t.name}, {t.city}</p>
                    <div className="flex items-center gap-0.5 text-amber-500">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 fill-amber-500" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats bar (brown) */}
      <section className="bg-[#5a3a1a] text-white">
        <div className="max-w-7xl mx-auto px-4 py-14 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((s, i) => {
            const Icon = statIcons[i] || Award;
            return (
              <div key={i} className="px-4">
                <div className="flex justify-center mb-3">
                  <Icon className="w-9 h-9 text-amber-300" />
                </div>
                <div className="text-4xl md:text-5xl font-extrabold text-amber-300">
                  {s.value.toLocaleString()}{s.suffix}
                </div>
                <p className="mt-2 text-white/85">{s.label}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Patient education */}
      <section className="py-20 px-4 bg-[#fbf6ee]">
        <div className="max-w-7xl mx-auto text-center mb-12">
          <p className="font-script text-emerald-700 text-2xl mb-2">Patient Education</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">Latest News & Articles</h2>
        </div>
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
          {articles.map((a) => (
            <article key={a.slug} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow">
              <div className="relative">
                <img src={a.image} alt={a.title} className="w-full h-56 object-cover" />
                <div className="absolute top-4 left-4 w-16 h-16 rounded-full bg-amber-400 flex flex-col items-center justify-center text-[#1a1a1a] font-bold leading-tight shadow-md">
                  <span className="text-xl">{a.day}</span>
                  <span className="text-[10px] uppercase">{a.month}</span>
                </div>
              </div>
              <div className="p-6">
                <p className="text-xs text-gray-500 mb-2">By {a.author}</p>
                <h3 className="text-lg font-bold text-gray-900 mb-3 leading-snug hover:text-[#b81d24] transition-colors">
                  <Link to={`/articles/${a.slug}`}>{a.title}</Link>
                </h3>
                <Link to={`/articles/${a.slug}`} className="inline-flex items-center gap-2 text-[#b81d24] font-semibold border-b-2 border-amber-400 pb-1 hover:gap-3 transition-all">
                  Continue Reading <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Contact + form */}
      <ContactBlock />
    </div>
  );
}

function ContactBlock() {
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", service: "", message: "", agree: false });
  const [sent, setSent] = React.useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.agree) return;
    setSent(true);
    setTimeout(() => setSent(false), 4000);
    setForm({ name: "", email: "", phone: "", service: "", message: "", agree: false });
  };

  return (
    <section className="py-20 px-4 bg-[#f3ece0]">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div className="rounded-2xl overflow-hidden shadow-md h-72">
            <iframe
              title="Clinic location"
              src="https://www.google.com/maps?q=Saket,Meerut,Uttar+Pradesh&output=embed"
              className="w-full h-full border-0"
              loading="lazy"
            />
          </div>
          <div className="bg-amber-300 rounded-2xl p-6 flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#1a1a1a] flex items-center justify-center flex-shrink-0">
              <MapPin className="w-6 h-6 text-amber-300" />
            </div>
            <div>
              <h4 className="font-bold text-[#1a1a1a]">Mamtesh Gupta Heart Center</h4>
              <p className="text-[#1a1a1a]/85 text-sm mt-1">{siteInfo.fullAddress}</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl p-5 border border-amber-100">
              <Phone className="w-6 h-6 text-[#b81d24] mb-2" />
              <p className="font-bold text-gray-900">Call Us</p>
              <a className="text-sm text-gray-600" href={`tel:${siteInfo.phone}`}>{siteInfo.phoneFull}</a>
            </div>
            <div className="bg-white rounded-2xl p-5 border border-amber-100">
              <Clock className="w-6 h-6 text-[#b81d24] mb-2" />
              <p className="font-bold text-gray-900">OPD Hours</p>
              <p className="text-sm text-gray-600">{siteInfo.hours.weekday}</p>
              <p className="text-sm text-gray-600">{siteInfo.hours.sunday}</p>
            </div>
          </div>
        </div>
        <div>
          <p className="font-script text-emerald-700 text-2xl mb-1">Book Appointment</p>
          <h3 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2">Contact Us Today!</h3>
          <p className="text-gray-600 mb-6">We will reply you within 24 hours via email, thank you for contacting</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name*" className="w-full bg-white rounded-full px-5 py-3.5 border border-amber-100 focus:outline-none focus:ring-2 focus:ring-[#b81d24]" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email*" className="w-full bg-white rounded-full px-5 py-3.5 border border-amber-100 focus:outline-none focus:ring-2 focus:ring-[#b81d24]" />
              <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone*" className="w-full bg-white rounded-full px-5 py-3.5 border border-amber-100 focus:outline-none focus:ring-2 focus:ring-[#b81d24]" />
              <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="w-full bg-white rounded-full px-5 py-3.5 border border-amber-100 focus:outline-none focus:ring-2 focus:ring-[#b81d24]">
                <option value="">Select Service</option>
                {serviceOptionsForForm.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Message..." className="w-full bg-white rounded-2xl px-5 py-3.5 border border-amber-100 focus:outline-none focus:ring-2 focus:ring-[#b81d24]" />
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input type="checkbox" checked={form.agree} onChange={(e) => setForm({ ...form, agree: e.target.checked })} className="w-4 h-4 accent-[#b81d24]" />
              Agree to our terms and conditions
            </label>
            <button type="submit" disabled={!form.agree} className="inline-flex items-center gap-2 bg-[#b81d24] hover:bg-[#9a1820] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold px-7 py-3.5 rounded-full transition-colors">
              Send Message
              <ArrowRight className="w-4 h-4" />
            </button>
            {sent && <p className="text-emerald-700 font-semibold">Thank you! Your message has been received.</p>}
          </form>
        </div>
      </div>
    </section>
  );
}
